import { Outlet, Link, useLocation } from 'react-router-dom'
import { displayName } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { registerNativePush } from '../lib/native'
import { useState, useEffect } from 'react'
import type { User } from '../lib/types'
import SageDock from './SageDock'
import CommandPalette from './CommandPalette'
import LockScreen from './LockScreen'
import Tour, { TOUR_DONE_KEY } from './Tour'
import { getTheme, setTheme, type Theme } from '../lib/theme'
import { api } from '../lib/api'
import { configureLock, touchActivity, shouldBeLocked, lockNow, isLockConfigured, getVeil, setVeilLocal, subscribeSecurity } from '../lib/security'

interface Props {
  user: User | null
  onLogout: () => void
}

export default function Layout({ user, onLogout }: Props) {
  const { pathname } = useLocation()
  const [sageOpen, setSageOpen] = useState(() => localStorage.getItem('fa_sage_open') === '1')
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [theme, setThemeState] = useState<Theme>(getTheme())
  const [locked, setLocked] = useState(false)
  const [tourOpen, setTourOpen] = useState(false)

  // First-run walkthrough: auto-offer once for approved users; re-entry via 'tour:start'
  useEffect(() => { registerNativePush() }, [])
  useEffect(() => {
    if (user?.approved && !localStorage.getItem(TOUR_DONE_KEY) && !(user as User & { tour_done?: number }).tour_done) {
      const t = setTimeout(() => setTourOpen(true), 1200)
      return () => clearTimeout(t)
    }
  }, [user])
  useEffect(() => {
    const onStart = () => setTourOpen(true)
    window.addEventListener('tour:start', onStart)
    return () => window.removeEventListener('tour:start', onStart)
  }, [])
  const [lockOn, setLockOn] = useState(false)
  const [veil, setVeil] = useState(getVeil())

  // Sanctum Lock: pull settings, then watch idle time + tab visibility
  useEffect(() => {
    if (!user) return
    api.getLock().then(s => {
      configureLock(s.enabled, s.timeout_mins)
      setLockOn(s.enabled)          // React state so the 🔒 control re-renders reliably
      setVeilLocal(s.privacy_veil)
      setVeil(s.privacy_veil)
      if (shouldBeLocked()) setLocked(true)
    }).catch(() => {})
    const unsub = subscribeSecurity(() => { setVeil(getVeil()); setLockOn(isLockConfigured()) })
    const onActivity = () => { if (!shouldBeLocked()) touchActivity() }
    const tick = setInterval(() => { if (shouldBeLocked()) setLocked(true) }, 15_000)
    const onVis = () => { if (document.hidden && isLockConfigured()) touchActivity(); else if (shouldBeLocked()) setLocked(true) }
    window.addEventListener('pointerdown', onActivity)
    window.addEventListener('keydown', onActivity)
    document.addEventListener('visibilitychange', onVis)
    touchActivity()
    return () => {
      unsub(); clearInterval(tick)
      window.removeEventListener('pointerdown', onActivity)
      window.removeEventListener('keydown', onActivity)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [user])

  useEffect(() => { localStorage.setItem('fa_sage_open', sageOpen ? '1' : '0') }, [sageOpen])

  // any view can summon the dock (e.g. “Review with Sage” after a GEDCOM import)
  useEffect(() => {
    const onOpen = () => setSageOpen(true)
    window.addEventListener('sage:open', onOpen)
    return () => window.removeEventListener('sage:open', onOpen)
  }, [])

  // ⌘K / Ctrl+K opens the wayfinder
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setThemeState(next)
  }

  // icon shown < sm so the bar fits a 360px phone (v0.16.2 — the store-screenshot
  // pass caught the desktop labels overflowing the header on real phone widths)
  const { t, locale, setLocale } = useI18n()
  const nav = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: '🏠' },
    { to: '/trees', label: t('nav.trees'), icon: '🌳' },
    { to: '/circles', label: t('nav.circles'), icon: '👪' },
    { to: '/calendar', label: t('nav.calendar'), icon: '🗓' },
  ]

  const showSage = !!user && !!user.approved

  return (
    <div className="h-screen flex flex-col bg-void-950">
      <header className="bg-void-900/80 backdrop-blur-xl border-b border-white/[0.06] z-40">
        <div className="max-w-[1700px] mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-1 min-w-0">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <span className="text-2xl">🌳</span>
            <span className="font-display font-semibold text-lg text-gradient-gold hidden sm:inline">Family Ancestry</span>
            <span className="font-display font-semibold text-lg text-gradient-gold sm:hidden">FA</span>
          </Link>

          <nav className="flex items-center gap-0.5 sm:gap-1 min-w-0">
            {user && nav.map(({ to, label, icon }) => (
              <Link
                key={to}
                to={to}
                data-tour={to === '/trees' ? 'nav-trees' : undefined}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  pathname === to || (to === '/trees' && pathname.startsWith('/trees'))
                    ? 'bg-white/10 text-gold-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                }`}
                title={label}
              >
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden text-base leading-none">{icon}</span>
              </Link>
            ))}
            {user?.approved && (
              <button onClick={() => setPaletteOpen(true)} title="Search everything (⌘K)" data-tour="search"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-white/10">
                🔍 <kbd className="hidden lg:inline text-[10px] text-slate-600">⌘K</kbd>
              </button>
            )}
            <button onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
              title={locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
              className="w-8 h-8 rounded-lg text-[11px] font-semibold flex items-center justify-center text-slate-400 hover:text-gold-300 hover:bg-white/5">
              {locale === 'ar' ? 'EN' : 'ع'}
            </button>
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              className="w-8 h-8 rounded-lg text-sm flex items-center justify-center text-slate-400 hover:text-gold-300 hover:bg-white/5">
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            {user?.approved && (
              <button onClick={async () => { const next = !veil; setVeil(next); setVeilLocal(next); api.setLock({ privacy_veil: next }).catch(() => {}) }}
                title={veil ? 'Privacy veil ON — names masked' : 'Privacy veil off — mask names'} data-tour="veil"
                className={`w-8 h-8 rounded-lg text-sm flex items-center justify-center ${veil ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-violet-300 hover:bg-white/5'}`}>
                {veil ? '🎭' : '👁'}
              </button>
            )}
            {user?.approved && lockOn && (
              <button onClick={() => { lockNow(); setLocked(true) }} title="Lock now"
                className="w-8 h-8 rounded-lg text-sm flex items-center justify-center text-slate-400 hover:text-gold-300 hover:bg-white/5">
                🔒
              </button>
            )}
            {showSage && (
              <button onClick={() => setSageOpen(o => !o)}
                title={sageOpen ? 'Hide Sage' : 'Summon Sage'} data-tour="sage"
                className={`px-2.5 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  sageOpen ? 'bg-gold-500/15 text-gold-300 border border-gold-500/30' : 'text-slate-400 hover:text-gold-300 hover:bg-white/5 border border-transparent'}`}>
                ✳ <span className="hidden sm:inline">Sage</span>
              </button>
            )}
            {user ? (
              <div className="flex items-center gap-1 ms-1">
                {user.role === 'admin' && (
                  <Link to="/admin" className={`px-2.5 py-1.5 rounded-lg text-sm font-medium ${pathname === '/admin' ? 'bg-white/10 text-gold-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}>
                    <span className="hidden sm:inline">Admin</span>
                    <span className="sm:hidden">⚙</span>
                  </Link>
                )}
                <Link to="/guide" title="The Fieldbook — how everything works" className={`px-2 py-1.5 rounded-lg text-sm ${pathname === '/guide' ? 'bg-white/10 text-gold-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}>
                  📖
                </Link>
                <Link to="/support" title="Help & Support" className={`px-2 py-1.5 rounded-lg text-sm ${pathname === '/support' ? 'bg-white/10 text-gold-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}>
                  ?
                </Link>
                <Link to="/profile" title="Profile" className="px-2.5 py-1.5 rounded-lg text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 max-w-[110px] truncate">
                  <span className="hidden sm:inline">{displayName(user)}</span>
                  <span className="sm:hidden">👤</span>
                </Link>
                <button onClick={onLogout} title="Sign out" className="px-2 py-1.5 text-sm text-slate-500 hover:text-slate-300">
                  <span className="hidden sm:inline">Logout</span>
                  <span className="sm:hidden">⏻</span>
                </button>
              </div>
            ) : (
              <Link to="/login" className="ms-2 btn-gold !py-1.5 text-sm">Sign In</Link>
            )}
          </nav>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <main className="flex-1 min-w-0 overflow-y-auto">
          <Outlet />
          <footer className="border-t border-white/[0.06] py-4 text-center text-xs text-slate-600">
            Family Ancestry &middot; your lineage, alive &middot; <Link to="/guide" data-tour="guide" className="hover:text-gold-400">fieldbook</Link> &middot; <Link to="/privacy" className="hover:text-gold-400">privacy</Link>
          </footer>
        </main>
        {showSage && <SageDock open={sageOpen} onClose={() => setSageOpen(false)} />}
      </div>
      {user?.approved && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
      {locked && <LockScreen onUnlock={() => setLocked(false)} />}
      {tourOpen && user?.approved && <Tour onClose={() => setTourOpen(false)} />}
    </div>
  )
}
